import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HubSpokeSvg } from './HubSpokeSvg';
import { useSpokePositions } from './useSpokePositions';
import styles from './HubSpoke.module.css';

interface CardData {
  title: string;
  subtitle: string;
}

export function HubSpoke() {
  const { t } = useTranslation('solutions');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const problemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const promiseRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);

  const problems = t('hubSpoke.problems', { returnObjects: true }) as CardData[];
  const promises = t('hubSpoke.promises', { returnObjects: true }) as CardData[];

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const setProblemRef = useCallback((i: number) => (el: HTMLDivElement | null) => {
    problemRefs.current[i] = el;
  }, []);

  const setPromiseRef = useCallback((i: number) => (el: HTMLDivElement | null) => {
    promiseRefs.current[i] = el;
  }, []);

  const paths = useSpokePositions({
    wrapperRef,
    centerRef,
    problemRefs,
    promiseRefs,
    bottomRef,
  });

  return (
    <div className={styles.section}>
      <div className={styles.wideContainer}>
        <div
          ref={wrapperRef}
          className={`${styles.wrapper} ${isVisible ? styles.visible : ''}`}
        >
          <div className={styles.grid}>
            <div className={styles.leftCol}>
              {problems.map((p, i) => (
                <div
                  key={i}
                  ref={setProblemRef(i)}
                  className={styles.problemCard}
                  style={{ '--card-index': i } as React.CSSProperties}
                >
                  <div className={styles.cardTitle}>{p.title}</div>
                  <div className={styles.cardSubtitle}>{p.subtitle}</div>
                </div>
              ))}
            </div>

            <div ref={centerRef} className={styles.hub}>
              <div className={styles.hubRing} style={{ '--ring-delay': '0s', width: 200, height: 200 } as React.CSSProperties} />
              <div className={styles.hubRing} style={{ '--ring-delay': '1s', width: 240, height: 240 } as React.CSSProperties} />
              <div className={styles.hubRing} style={{ '--ring-delay': '2s', width: 280, height: 280 } as React.CSSProperties} />
              <div className={styles.hubCircle}>
                <img
                  src="/images/logos/logo-blanc.webp"
                  alt="QBP Solutions"
                  className={styles.hubLogo}
                />
              </div>
            </div>

            <div className={styles.rightCol}>
              {promises.map((p, i) => (
                <div
                  key={i}
                  ref={setPromiseRef(i)}
                  className={styles.promiseCard}
                  style={{ '--card-index': i } as React.CSSProperties}
                >
                  <div className={styles.cardTitle}>{p.title}</div>
                  <div className={styles.cardSubtitle}>{p.subtitle}</div>
                </div>
              ))}
            </div>
          </div>

          {paths && <HubSpokeSvg paths={paths} isVisible={isVisible} />}

          <div ref={bottomRef} className={styles.solution}>
            <h3 className={styles.solutionTitle}>
              {t('hubSpoke.solutionTitle')}
            </h3>
            <p className={styles.solutionSubtitle}>
              {t('hubSpoke.solutionSubtitle')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
